import type { Id } from '@odhaczka/core';
import { useEffect, useReducer, useState } from 'react';
import { useNavigate } from 'react-router';
import { GeoSection } from '../components/GeoSection';
import { LidlSection } from '../components/LidlSection';
import { BottomNav, SyncBadge } from '../components/Layout';
import { cardCls, inputCls, primaryBtn, secondaryBtn, sectionTitleCls } from '../components/ui/classes';
import { householdErrorMessage } from '../components/ui/householdErrors';
import { PageHeader } from '../components/ui/PageHeader';
import { SkeletonRows } from '../components/ui/Skeleton';
import {
  cancelInvite,
  inviteMember,
  leaveHousehold,
  memberFirstName,
  memberInitial,
  refreshHousehold,
  removeMember,
  renameHousehold,
  useHousehold,
  type HouseholdInfo,
  type Member,
  type PendingInvite,
} from '../data/household';
import { signOut, useAuth } from '../lib/auth';
import { disablePush, enablePush, usePushEnabled, usePushSupport } from '../lib/push';
import { useSyncStatus } from '../sync/useSyncStatus';

const DAY_MS = 24 * 60 * 60 * 1000;

export function HomePage() {
  const info = useHousehold();
  const auth = useAuth();
  const me = auth.status === 'signed-in' ? auth.session : null;
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    void refreshHousehold();
  }, []);

  const retry = async () => {
    setRetrying(true);
    await refreshHousehold();
    setRetrying(false);
  };

  return (
    <>
      <PageHeader title="Dom" right={<SyncBadge />} />
      <main className="flex flex-1 flex-col gap-6 p-4">
        {info === undefined ? (
          <SkeletonRows />
        ) : info === null ? (
          <div className={`${cardCls} flex flex-col gap-3 p-4`}>
            <p className="text-stone-600 dark:text-stone-400">Nie udało się wczytać domu. Do tego potrzebny jest internet.</p>
            <button type="button" onClick={retry} disabled={retrying} className={secondaryBtn}>
              {retrying ? 'Wczytuję…' : 'Spróbuj ponownie'}
            </button>
          </div>
        ) : (
          <>
            <HouseholdName name={info.household.name} />
            <Members info={info} meId={me?.userId ?? null} />
            <Invites invites={info.invites} />
          </>
        )}
        <LidlSection />
        <PushSection />
        <GeoSection />
        <Account email={me?.email ?? null} />
      </main>
      <BottomNav />
    </>
  );
}

function HouseholdName({ name }: { name: string }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (e: { preventDefault(): void }) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await renameHousehold(value);
      setEditing(false);
    } catch (err) {
      setError(householdErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (editing)
    return (
      <form onSubmit={save} className={`${cardCls} flex flex-col gap-3 p-4`}>
        <label className="flex flex-col gap-1">
          <span className="px-1 text-sm font-medium text-stone-600 dark:text-stone-400">Nazwa domu</span>
          <input autoFocus value={value} onChange={(e) => setValue(e.target.value)} placeholder="np. Nowiccy" enterKeyHint="done" className={inputCls} />
        </label>
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => setEditing(false)} className={secondaryBtn}>
            Anuluj
          </button>
          <button disabled={busy || !value.trim()} className={primaryBtn}>
            Zapisz
          </button>
        </div>
      </form>
    );

  return (
    <div className={`${cardCls} flex items-center gap-3 p-4`}>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-stone-500">Twój dom</div>
        <div className="truncate text-xl font-bold">{name}</div>
      </div>
      <button
        type="button"
        onClick={() => {
          setValue(name);
          setEditing(true);
        }}
        className="min-h-11 shrink-0 rounded-lg px-3 text-sm font-medium text-brand-700 active:bg-brand-50 dark:text-brand-500 dark:active:bg-stone-800"
      >
        Zmień nazwę
      </button>
    </div>
  );
}

function Members({ info, meId }: { info: HouseholdInfo; meId: Id | null }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const members = [...info.members].sort(
    (a, b) => Number(b.userId === meId) - Number(a.userId === meId) || a.name.localeCompare(b.name, 'pl'),
  );
  const shared = members.length > 1;

  const run = async (key: string, action: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(householdErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const remove = (m: Member) => {
    const name = memberFirstName(m);
    if (!confirm(`Usunąć z domu: ${name}?\n\nTa osoba straci dostęp do wspólnych list i sklepów.`)) return;
    void run(m.userId, () => removeMember(m.userId));
  };

  const leave = () => {
    const msg = `Opuścić dom „${info.household.name}”?\n\nWspólne listy i sklepy znikną z tego telefonu (zostaną u pozostałych domowników). Zaczniesz od pustego domu.`;
    if (!confirm(msg)) return;
    void run('leave', async () => {
      await leaveHousehold();
      navigate('/', { replace: true });
    });
  };

  return (
    <section className="flex flex-col gap-2">
      <h2 className={sectionTitleCls}>Domownicy</h2>
      <ul className={`${cardCls} divide-y divide-stone-200 dark:divide-stone-800`}>
        {members.map((m) => {
          const isMe = m.userId === meId;
          return (
            <li key={m.userId} className="flex items-center gap-3 px-4 py-3">
              <span
                aria-hidden
                className="flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-100 font-semibold text-brand-800 dark:bg-brand-800/30 dark:text-brand-100"
              >
                {memberInitial(m)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium">{m.name || memberFirstName(m)}</span>
                  {isMe && (
                    <span className="shrink-0 rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-600 dark:bg-stone-800 dark:text-stone-300">
                      Ty
                    </span>
                  )}
                </div>
                <div className="truncate text-sm text-stone-500">{m.email}</div>
              </div>
              {!isMe && (
                <button
                  type="button"
                  onClick={() => remove(m)}
                  disabled={busy !== null}
                  className="min-h-11 shrink-0 rounded-lg px-2 text-sm font-medium text-red-600 active:bg-red-50 disabled:opacity-50 dark:text-red-400 dark:active:bg-stone-800"
                >
                  Usuń z domu
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {!shared && <p className="px-1 text-sm text-stone-500">Na razie w domu jest tylko jedna osoba. Zaproś domownika, żeby robić wspólne listy.</p>}
      {error && <p className="px-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
      {shared && (
        <button
          type="button"
          onClick={leave}
          disabled={busy !== null}
          className="min-h-11 self-start px-1 text-sm font-medium text-red-600 disabled:opacity-50 dark:text-red-400"
        >
          {busy === 'leave' ? 'Opuszczam dom…' : 'Opuść dom'}
        </button>
      )}
    </section>
  );
}

function Invites({ invites }: { invites: PendingInvite[] }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const send = async (e: { preventDefault(): void }) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSentTo(null);
    try {
      const invite = await inviteMember(email);
      setSentTo(invite.email);
      setEmail('');
    } catch (err) {
      setError(householdErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (inv: PendingInvite) => {
    if (!confirm(`Anulować zaproszenie dla ${inv.email}?`)) return;
    try {
      await cancelInvite(inv.id);
    } catch (err) {
      setError(householdErrorMessage(err));
    }
  };

  return (
    <section className="flex flex-col gap-2">
      <h2 className={sectionTitleCls}>Zaproś domownika</h2>
      <form onSubmit={send} className={`${cardCls} flex flex-col gap-3 p-4`}>
        <p className="text-sm text-stone-600 dark:text-stone-400">
          Wyślemy e-mail z linkiem. Po dołączeniu będziecie mieć wspólne listy i sklepy, a w trakcie zakupów zobaczycie, co już jest w koszyku.
        </p>
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="adres@email.pl"
          enterKeyHint="send"
          className={inputCls}
        />
        {sentTo && <p className="text-sm text-brand-700 dark:text-brand-500">Wysłano zaproszenie na {sentTo}.</p>}
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        <button disabled={busy || !email.trim()} className={primaryBtn}>
          {busy ? 'Wysyłam…' : 'Wyślij zaproszenie'}
        </button>
      </form>

      {invites.length > 0 && (
        <>
          <h3 className={`${sectionTitleCls} mt-2`}>Czekają na odpowiedź</h3>
          <ul className={`${cardCls} divide-y divide-stone-200 dark:divide-stone-800`}>
            {invites.map((inv) => (
              <li key={inv.id} className="flex items-center gap-3 px-4 py-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{inv.email}</div>
                  <div className="text-sm text-stone-500">{expiryLabel(inv.expiresMs)}</div>
                </div>
                <button
                  type="button"
                  onClick={() => void cancel(inv)}
                  className="min-h-11 shrink-0 rounded-lg px-3 text-sm font-medium text-stone-600 active:bg-stone-100 dark:text-stone-300 dark:active:bg-stone-800"
                >
                  Anuluj
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function expiryLabel(expiresMs: number): string {
  const left = expiresMs - Date.now();
  if (left <= 0) return 'wygasło';
  const days = Math.ceil(left / DAY_MS);
  return `ważne jeszcze ${days} ${days === 1 ? 'dzień' : 'dni'}`;
}

const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
const browserHasPush = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

function unsupportedText(): string {
  // 'unsupported' przy obsługującej przeglądarce = serwer bez kluczy VAPID.
  if (browserHasPush()) return 'Powiadomienia są chwilowo wyłączone na serwerze.';
  if (isIos())
    return 'Na iPhonie powiadomienia działają tylko w aplikacji dodanej do ekranu początkowego. W Safari stuknij „Udostępnij” → „Do ekranu początkowego”, a potem otwórz Odhaczkę z ikony i wróć tutaj.';
  return 'Ta przeglądarka nie obsługuje powiadomień. Otwórz Odhaczkę w Chrome albo zainstaluj ją jako aplikację.';
}

function PushSection() {
  const enabled = usePushEnabled();
  const support = usePushSupport();
  const [, recheck] = useReducer((n: number) => n + 1, 0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Zgodę można zmienić w ustawieniach przeglądarki — po powrocie odczytujemy ją ponownie.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') recheck();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const toggle = async () => {
    setBusy(true);
    setError(null);
    try {
      if (enabled) await disablePush();
      else await enablePush();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Nie udało się zmienić ustawienia powiadomień.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flex flex-col gap-2">
      <h2 className={sectionTitleCls}>Powiadomienia o brakach</h2>
      <div className={`${cardCls} overflow-hidden`}>
        {support === 'unsupported' ? (
          <p className="p-4 text-sm text-stone-600 dark:text-stone-400">{unsupportedText()}</p>
        ) : support === 'denied' ? (
          <div className="flex flex-col gap-1 p-4 text-sm text-stone-600 dark:text-stone-400">
            <p className="font-medium text-stone-900 dark:text-stone-100">Powiadomienia są zablokowane.</p>
            <p>
              Odblokuj je w przeglądarce: stuknij ikonę obok adresu strony → Uprawnienia → Powiadomienia → Zezwalaj. W zainstalowanej aplikacji:
              Ustawienia telefonu → Aplikacje → Odhaczka → Powiadomienia.
            </p>
          </div>
        ) : (
          <button
            type="button"
            role="switch"
            aria-checked={!!enabled}
            disabled={busy || enabled === undefined}
            onClick={() => void toggle()}
            className="flex min-h-14 w-full items-center gap-3 px-4 py-3 text-left active:bg-stone-50 disabled:opacity-60 dark:active:bg-stone-800"
          >
            <span className="min-w-0 flex-1">
              <span className="block font-medium">Na tym telefonie</span>
              <span className="block text-sm text-stone-500">
                Dam znać, gdy w sklepie czegoś zabraknie, ktoś zaproponuje zamiennik albo dopisze coś do listy.
              </span>
            </span>
            <span
              aria-hidden
              className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${enabled ? 'bg-brand-600' : 'bg-stone-300 dark:bg-stone-700'}`}
            >
              <span
                className={`absolute top-0.5 left-0.5 size-6 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-5' : ''}`}
              />
            </span>
          </button>
        )}
        {error && <p className="px-4 pb-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
      </div>
    </section>
  );
}

function Account({ email }: { email: string | null }) {
  const sync = useSyncStatus();
  const [busy, setBusy] = useState(false);

  const logout = async () => {
    const msg =
      sync.pending > 0 && !sync.online
        ? 'Telefon jest offline, a część zmian nie została jeszcze wysłana. Po wylogowaniu przepadną.\n\nWylogować mimo to?'
        : 'Wylogować się? Listy znikną z tego telefonu do następnego logowania.';
    if (!confirm(msg)) return;
    setBusy(true);
    await signOut();
  };

  return (
    <section className="mt-auto flex flex-col gap-2 pt-4">
      {email && <p className="text-center text-sm text-stone-500">Zalogowano jako {email}</p>}
      <button type="button" onClick={() => void logout()} disabled={busy} className={`${secondaryBtn} text-red-600 dark:text-red-400`}>
        {busy ? 'Wylogowuję…' : 'Wyloguj'}
      </button>
    </section>
  );
}
